import type { MarkersPoints } from 'plyr'
import type { VNode, VNodeChild } from 'vue'
import type { IconName } from './icon-names'

export interface Menu {
  text: string
  href: string
  hrefPartForActivation: string
  icon?: IconName
  class?: string
  isShowCount?: boolean
  excludeCountries?: string[]
  supType?: string
}

export interface Countries {
  ru: string
  uk: string
  kz: string
  ua: string
  by: string
}

export type UserStatus = 'identified' | 'unidentified'

export interface Profile {
  id: number
  status: string
  name: string
  countryCode: string
  avatar?: string
  currency: string
  currencySymbol: CurrencySymbol
  balance: string
  slowdown: Slowdown | false
  unidentified_markup: string
}

export interface RequestProfile {
  id: number
  country_code: string
  is_identificated: boolean
  username: string
  unidentified_markup: string
}

export interface Slowdown {
  slowdown_edge: string
}

export interface UserData {
  avg_billsec_in: number
  avg_billsec_out: number
  avg_cost_outgoing_calls: number
  calls_cost: number
  calls_count: number
  incoming_calls: number
  cost_incoming_calls: number
  other_cost: number
  outgoing_calls: number
  sms_cost: number
  daily_calls_count: number[]
  avg_cost_calls: number
  avg_cost_in: number
  avg_cost_out: number
  calls_by_day: number[]
  spending_by_day: string[]
  cost_outgoing_calls: number
}

export interface Expenses {
  [key: string]: ExpensesItem
}

export interface ExpensesItem {
  value: number
  color: string
  label: string
}

export interface LegendItem {
  color: string
  text: string
}

export interface ToastTypes {
  info: string
  success: string
  warning: string
  normal: string
}

export interface GlobalNotification {
  id: number
  text: string
  type: string
}

export interface NotificationParams {
  openedHeight?: number
  closedHeight?: number
}

export interface DateRange {
  start: string | number | Date
  end: string | number | Date
}

export interface Discount {
  discount: boolean
  details?: DiscountDetails
}

export interface DiscountDetails {
  amount: string
  amount_payeed: string
  countdown_days: string
  is_identification_required: boolean
  is_identificated: boolean
  callers_cities: Callers[]
  callers_region: Callers[]
}

interface Callers {
  id: number
  name: string
}

export interface TechMessageNotification {
  countable_chats: number
  has_uncountable_chats: boolean
}

export interface TableColumn<T = any> {
  title: string | (() => VNode | VNodeChild)
  key: string
  width?: string
  searchable?: boolean
  customRender?: ({ record }: { record: T }) => VNode | VNodeChild
}

export type TableLayout = 'fixed' | 'auto'

export interface Audio {
  error?: string
  url: string
}

export type AuthenticationType = 'login' | 'registration'

export enum LanguagesLocales {
  RU = 'ru-RU',
  UA = 'uk-UA',
  KZ = 'kk-KZ',
  EN = 'en-US',
  BY = 'be-BY',
}

export interface Country {
  name: string
  icon: string
  englishName: string
  locale: string
  code: CountryCode
  currencySymbol: CurrencySymbol
}

export type CountryCode = 'RU' | 'UA' | 'BY' | 'KZ'

export enum CountryCodes {
  RU = 'RU',
  UA = 'UA',
  BY = 'BY',
  KZ = 'KZ',
}

export type CountryMasks = Record<CountryCode, string>

export enum Pages {
  DASHBOARD = 'dashboard',
  BALANCE = 'balance',
  CLASSIC = 'classic',
  DOCS = 'docs',
  PAYMENTS = 'payments',
  CALLERS = 'callers',
  PHONE_LIST = 'phone_list',
  PHONE_LIST_DETAIL = 'phone_list_detail',
  LIMBO = 'limbo',
  CAMPAIGN_TYPE = 'campaign_type',
  AI = 'ai',
  PRICES = 'prices',
  AUDIO_CLIPS = 'audio_clips',
  AUDIO_CLIPS_EDIT = 'audioclip_edit',
  MANAGERS = 'managers',
  DETAILING = 'detailing',
  INCOMING_AI = 'incoming_ai',
  SIP = 'sip',
  MODERN = 'modern',
  LOGS = 'log_list',
}

export type CurrencySymbol = '₽' | '₴' | 'Br' | '₸' | ''

export type AudioClip =
  'synth' | 'uploaded' | 'waiting' | 'speaker' | 'reworked' | 'upread' | 'reread' | 'upreaded' | 'rereaded'

export interface ItemFile {
  id?: string
  progress?: number
  file: File
}

export enum CampaignTypes {
  UI = 'ui',
  CLASSIC = 'classic',
  CROSS = 'cross',
  API = 'api',
  EUROPE = 'europe',
  INCOMING = 'incoming',
  FLASH_CALL = 'flashcall',
  PINCODE = 'pincode',
  TELL_CODE = 'tellcode',
  PINCODE_SHORT = 'pincode_short',
  PINCODE_INCOMING = 'pincode_incoming',
  AI = 'ai',
  INCOMING_AI = 'incoming_ai',
  MODERN = 'modern',
}

export enum Genders {
  MALE = 'male',
  FEMALE = 'female',
}

export enum AppModalV2Animations {
  FADE = 'app-modal-v2-fade',
  SLIDE = 'app-modal-v2-slide',
}

export interface StepData {
  label: string
  disabled: boolean
}

export enum PhoneType {
  ABC = 'abc',
  DEF = 'def',
}

export type TimeCodeData = MarkersPoints

export type TranslateFunction = (key: string, params?: Record<string, unknown>) => string

export enum Operators {
  MTT = 'mtt',
  MTS = 'mts',
  TELE2 = 'tele2',
  MGF = 'mgf',
  BLN = 'bln',
  TINKOFF = 'tinkoff',
  // Беларусь
  VELCOM = 'velcom',
  MTSBY = 'mtsby',
  LIFEBY = 'lifeby',
  // Казахстан
  KARTEL = 'kartel',
  KCELL = 'kcell',
  TELE2KZ = 'tele2kz',
  // Украина
  VODAFONE = 'vodafone',
  KYIVSTAR = 'uakyiv',
  LIFECELL = 'lifecell',
}

export interface OperatorData {
  // null, если для оператора ещё нет иконки
  icon: IconName | null
  nameKey: string
  country: CountryCode
}

export interface TabsContext {
  selected: string
  titles: string[]
}

export interface DatePeriodOption {
  value: string
  label: string
  from_date: string | Date
  to_date: string | Date
}

export enum ServiceKey {
  Synth = 'synth',
  SynthPremium = 'synth_premium',
  CallConnectionPrior = 'call_connection_prior',
  Transcribing = 'transcribing',
  TranscribingPremium = 'transcribing_premium',
  TranscribingInhouse = 'transcribing_inhouse',
  FasProtection = 'fas_protection',
  Label = 'label',
  AiDetectAa = 'ai_detect_aa',
  NoIdent = 'no_ident',
  BaseCaller = 'base_caller',
  PremiumCaller = 'premium_caller',
  TrashCallDetector = 'trash_call_detector',
}

export enum SocialMedia {
  TG = 'tg',
  MAX = 'max',
  VK = 'vk',
  DZEN = 'dzen',
  VC = 'vc',
}
